(function () {
  'use strict';

  // ── HoverIntent ──────────────────────────────────────────────────────────────
  // Desktop hover-intent: 150ms open delay, 100ms close grace per item.
  // Prevents flash when the mouse moves across items and handles diagonal
  // movement into the dropdown panel gracefully.

  function HoverIntent(navEl) {
    if (!navEl) return;

    var OPEN_DELAY  = 150;
    var CLOSE_DELAY = 300;
    var timers = new WeakMap();
    var items  = navEl.querySelectorAll('[data-nav-item]');

    function clearTimer(el) {
      clearTimeout(timers.get(el));
      timers.delete(el);
    }

    function closeItem(item) {
      var trigger = item.querySelector('.nav-primary__link');
      item.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    function closeAll(except) {
      items.forEach(function (item) {
        if (item !== except) closeItem(item);
      });
    }

    function activateSecond(secondItem) {
      var panel = secondItem.closest
        ? secondItem.closest('.nav-primary__dropdown')
        : (function () {
            var el = secondItem.parentNode;
            while (el && !el.classList.contains('nav-primary__dropdown')) el = el.parentNode;
            return el;
          }());
      if (!panel) return;

      panel.querySelectorAll('[data-nav-second]').forEach(function (el) {
        el.classList.remove('is-active');
      });
      panel.querySelectorAll('[data-nav-panel]').forEach(function (el) {
        el.classList.remove('is-active');
      });

      secondItem.classList.add('is-active');

      var handle = secondItem.getAttribute('data-nav-second');
      if (handle) {
        var thirdPanel = panel.querySelector('[data-nav-panel="' + handle + '"]');
        if (thirdPanel) thirdPanel.classList.add('is-active');
      }
    }

    function openItem(item) {
      var trigger = item.querySelector('.nav-primary__link');
      closeAll(item);
      item.classList.add('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      // Pre-activate first second-level item that has children so panel isn't empty
      var first = item.querySelector('[data-nav-second].nav-primary__second-item--has-children');
      if (first) activateSecond(first);
    }

    items.forEach(function (item) {
      if (!item.querySelector('.nav-primary__dropdown')) return;

      item.addEventListener('mouseenter', function () {
        clearTimer(item);
        timers.set(item, setTimeout(function () { openItem(item); }, OPEN_DELAY));
      });

      item.addEventListener('mouseleave', function () {
        clearTimer(item);
        timers.set(item, setTimeout(function () { closeItem(item); }, CLOSE_DELAY));
      });

      // Instant switch between second-level items inside an already-open panel
      item.querySelectorAll('[data-nav-second].nav-primary__second-item--has-children').forEach(function (secondItem) {
        secondItem.addEventListener('mouseenter', function () {
          activateSecond(secondItem);
        });
      });
    });

    // Escape closes open panel and returns focus to its trigger
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = navEl.querySelector('[data-nav-item].is-open');
      if (!open) return;
      closeItem(open);
      var trigger = open.querySelector('.nav-primary__link');
      if (trigger) trigger.focus();
    });

    // Click anywhere outside the nav closes all panels
    document.addEventListener('click', function (e) {
      if (!navEl.contains(e.target)) closeAll();
    });
  }

  // ── MobileDrawer ─────────────────────────────────────────────────────────────
  // Slide-in drawer with overlay, no page push.
  // Focus trap keeps keyboard navigation inside the open drawer.

  function MobileDrawer() {
    var drawer  = document.getElementById('NavPrimaryDrawer');
    var overlay = document.getElementById('NavDrawerOverlay');
    var openBtn = document.querySelector('.js-nav-drawer-open');
    if (!drawer || !overlay || !openBtn) return;

    var isOpen = false;

    function getFocusable() {
      return Array.prototype.slice.call(
        drawer.querySelectorAll('a[href], button:not([disabled])')
      );
    }

    function open() {
      isOpen = true;
      drawer.setAttribute('aria-hidden', 'false');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('nav-drawer-is-open');
      openBtn.setAttribute('aria-expanded', 'true');
      var focusable = getFocusable();
      if (focusable.length) focusable[0].focus();
    }

    function close() {
      isOpen = false;
      drawer.setAttribute('aria-hidden', 'true');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('nav-drawer-is-open');
      openBtn.setAttribute('aria-expanded', 'false');
      openBtn.focus();
    }

    openBtn.addEventListener('click', function () { isOpen ? close() : open(); });
    overlay.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (!isOpen) return;

      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;

      var focusable = getFocusable();
      if (!focusable.length) return;
      var first = focusable[0];
      var last  = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  // ── NavProductSlider ─────────────────────────────────────────────────────────
  // Adds prev/next arrow buttons to each [data-product-slider] element.
  // The track itself scrolls natively; buttons call scrollBy for smooth paging.

  function NavProductSlider() {
    var CARD_WIDTH = 142; // 130px card + 12px gap

    document.querySelectorAll('[data-product-slider]').forEach(function (slider) {
      var track = slider.querySelector('.nav-primary__product-track');
      if (!track) return;

      var prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'nav-primary__slider-btn nav-primary__slider-btn--prev';
      prev.setAttribute('aria-label', 'Previous products');
      prev.innerHTML = '<svg viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6.5,2 3.5,5 6.5,8"/></svg>';

      var next = document.createElement('button');
      next.type = 'button';
      next.className = 'nav-primary__slider-btn nav-primary__slider-btn--next';
      next.setAttribute('aria-label', 'Next products');
      next.innerHTML = '<svg viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3.5,2 6.5,5 3.5,8"/></svg>';

      slider.appendChild(prev);
      slider.appendChild(next);

      function updateButtons() {
        var atStart = track.scrollLeft <= 2;
        var atEnd   = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        prev.style.opacity       = atStart ? '0' : '1';
        prev.style.pointerEvents = atStart ? 'none' : '';
        next.style.opacity       = atEnd   ? '0' : '1';
        next.style.pointerEvents = atEnd   ? 'none' : '';
      }

      prev.addEventListener('click', function () {
        track.scrollBy({ left: -(CARD_WIDTH * 2), behavior: 'smooth' });
      });

      next.addEventListener('click', function () {
        track.scrollBy({ left: CARD_WIDTH * 2, behavior: 'smooth' });
      });

      track.addEventListener('scroll', updateButtons);
      updateButtons();
    });
  }

  // ── Accordion ────────────────────────────────────────────────────────────────
  // Handles chevron expand/collapse inside the mobile drawer.
  // Uses event delegation on the drawer — no per-item listeners.
  // CSS max-height transition animates the expand; no JS height measurement needed.

  function Accordion() {
    var drawer = document.getElementById('NavPrimaryDrawer');
    if (!drawer) return;

    drawer.addEventListener('click', function (e) {
      // Walk up from click target to find chevron button
      var btn = null;
      if (e.target.closest) {
        btn = e.target.closest('.nav-drawer__chevron');
      } else {
        var el = e.target;
        while (el && el !== drawer) {
          if (el.classList && el.classList.contains('nav-drawer__chevron')) { btn = el; break; }
          el = el.parentNode;
        }
      }
      if (!btn) return;

      var expanded = btn.getAttribute('aria-expanded') === 'true';
      var targetEl = document.getElementById(btn.getAttribute('aria-controls'));
      if (!targetEl) return;

      btn.setAttribute('aria-expanded', String(!expanded));
      targetEl.setAttribute('aria-hidden', String(expanded));
      targetEl.classList.toggle('is-expanded', !expanded);
    });
  }

  // ── Init ─────────────────────────────────────────────────────────────────────

  function init() {
    new HoverIntent(document.getElementById('NavPrimary'));
    new NavProductSlider();
    new MobileDrawer();
    new Accordion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}());
